import React from 'react';
import { Modal, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { MessageTemplate } from 'src/models/messageTemplate';
import { useMessageTemplateList } from 'src/usecases/messageTemplate/reader';

interface Props {
    open: boolean;
    onClose: () => void;
    onSelect: (messageTemplate: MessageTemplate) => void;
}

/**
 * テーブル列定義を作成する関数
 */
const createColumns = (uniqueCategories: string[]): ColumnsType<MessageTemplate> => [
    {
        title: 'タイトル',
        dataIndex: 'title',
        key: 'title',
        width: 350,
        ellipsis: true,
    },
    {
        title: 'カテゴリ',
        dataIndex: 'category',
        key: 'category',
        width: 150,
        filters: uniqueCategories.map(category => ({
            text: category,
            value: category,
        })),
        onFilter: (value, record) => record.category === value,
    },
    {
        title: 'タイプ',
        dataIndex: ['broadCastMessage', 'type'],
        key: 'type',
        width: 250,
        filters: [
            { value: 'text', label: 'テキストのみ' },
            { value: 'image', label: '画像のみ' },
            { value: 'template', label: 'アクション付き画像のみ（クーポンなど）' },
            { value: 'textAndImage', label: 'テキスト＆画像' },
            { value: 'textAndTemplate', label: 'テキスト＆アクション付き画像' }
        ].map(option => ({
            text: option.label,
            value: option.value
        })),
        onFilter: (value, record) => record.broadCastMessage?.type === value,
        render: (type: string) => {
            const typeMap = {
                text: 'テキストのみ',
                image: '画像のみ',
                template: 'アクション付き画像のみ（クーポンなど）',
                textAndImage: 'テキスト＆画像',
                textAndTemplate: 'テキスト＆アクション付き画像',
            };
            return typeMap[type as keyof typeof typeMap] || '不明';
        }
    },
    {
        title: '作成日時',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 150,
        sorter: (a, b) => {
            const dateA = dayjs(a.createdAt);
            const dateB = dayjs(b.createdAt);
            return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
        },
        render: (date: Date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    },
];

export const BroadcastTemplateModal: React.FC<Props> = ({ open, onClose, onSelect }) => {
    const values = useMessageTemplateList();

    // フィルタリング処理をメモ化
    const filteredTemplates = React.useMemo(() => {
        return values.data?.messageTemplates.filter(
            (template) => template.usage === 'broadcast'
        ) ?? [];
    }, [values.data?.messageTemplates]);

    // 使用されているカテゴリを取得
    const uniqueCategories = React.useMemo(() => {
        const categories = new Set<string>();
        filteredTemplates.forEach(template => {
            if (template.category) {
                categories.add(template.category);
            }
        });
        return Array.from(categories).sort();
    }, [filteredTemplates]);

    // テーブル列定義をメモ化（カテゴリフィルターを含む）
    const columns = React.useMemo(() => createColumns(uniqueCategories), [uniqueCategories]);

    // テンプレート選択ハンドラをメモ化
    const handleSelectTemplate = React.useCallback(
        (template: MessageTemplate) => {
            onSelect(template);
            onClose();
        },
        [onSelect, onClose]
    );

    // テーブル行の設定をメモ化
    const onRow = React.useCallback(
        (record: MessageTemplate) => ({
            onClick: () => handleSelectTemplate(record),
            style: { cursor: 'pointer' },
        }),
        [handleSelectTemplate]
    );

    return (
        <Modal
            title="テンプレート選択"
            open={open}
            onCancel={onClose}
            footer={null}
            width={1000}
        >
            <Table<MessageTemplate>
                columns={columns}
                dataSource={filteredTemplates}
                rowKey="id"
                pagination={{
                    pageSize: 10,
                    showSizeChanger: false,
                  }}
                onRow={onRow}
                size="middle"
                scroll={{ y: 400 }}
            />
        </Modal>
    );
};